import { cn } from '@/lib/utils';
import type { CandidateTrail, DiscoveryStep } from '@rock/types';
import { type FC, useState } from 'react';
import { describeUrl } from './documentShared';

/**
 * The discovery trail, rendered the way the bake-off review page taught us to read it: one lettered step
 * per line — the Google search, the result clicked (with its rank), the page read (and whether it stated
 * the model), the link followed (with the label a human would have clicked), the PDF fetched. The newest
 * path renders in full; earlier paths (re-sightings on later attempts) tuck behind a toggle.
 */
export const TrailList: FC<{ trails: CandidateTrail[] }> = ({ trails }) => {
    const [earlierOpen, setEarlierOpen] = useState(false);
    const [newest, ...earlier] = trails;
    if (!newest) return null;

    return (
        <div className="space-y-2">
            <TrailLegend />
            <TrailSteps trail={newest} />
            {earlier.length > 0 && (
                <div className="space-y-2">
                    <button
                        type="button"
                        onClick={() => setEarlierOpen((open) => !open)}
                        className="text-xs text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
                    >
                        {earlierOpen ? 'Hide earlier paths' : `Earlier paths (${earlier.length})`}
                    </button>
                    {earlierOpen &&
                        earlier.map((trail) => (
                            <div key={trail.at} className="border-l-2 border-muted pl-3">
                                <div className="mb-1 text-[11px] text-muted-foreground">
                                    {new Date(trail.at).toLocaleString()}
                                </div>
                                <TrailSteps trail={trail} />
                            </div>
                        ))}
                </div>
            )}
        </div>
    );
};

/** What each marker means, taught where it's read — only visible inside an opened trail panel. */
const TrailLegend: FC = () => (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
        <StepChip>S</StepChip> search
        <StepChip>#n</StepChip> clicked result
        <StepChip>P</StepChip> page read
        <StepChip>L</StepChip> link followed
        <StepChip>D</StepChip> PDF verified
    </div>
);

const TrailSteps: FC<{ trail: CandidateTrail }> = ({ trail }) => (
    <ul className="space-y-1.5">
        {/* Steps have no stable ids and the recorded list is immutable — index keys are safe. */}
        {trail.steps.map((step, index) => (
            <li key={index} className="flex min-w-0 items-start gap-2 text-xs">
                <TrailStep step={step} />
            </li>
        ))}
    </ul>
);

const TrailStep: FC<{ step: DiscoveryStep }> = ({ step }) => {
    switch (step.kind) {
        case 'search':
            return (
                <>
                    <StepChip>S</StepChip>
                    <span className="min-w-0 [overflow-wrap:anywhere]">
                        <span className="font-mono">“{step.query}”</span>{' '}
                        <span className="text-muted-foreground">
                            → {step.resultCount} result(s)
                            {step.restrictToUs ? ' · US-restricted' : ''}
                        </span>
                    </span>
                </>
            );
        case 'result':
            return (
                <>
                    <StepChip>#{step.position}</StepChip>
                    <span className="min-w-0 [overflow-wrap:anywhere]">
                        <TrailLink href={step.url}>{step.title ?? hostOf(step.url)}</TrailLink>
                        {step.title && <span className="text-muted-foreground"> {hostOf(step.url)}</span>}
                    </span>
                </>
            );
        case 'page':
            return (
                <>
                    <StepChip>P</StepChip>
                    <span className="min-w-0 [overflow-wrap:anywhere]">
                        <TrailLink href={step.url}>{hostOf(step.url)}</TrailLink>
                        {step.mentionsModel !== undefined && (
                            <span
                                className={cn(
                                    'ml-1',
                                    step.mentionsModel ? 'text-emerald-700' : 'text-muted-foreground',
                                )}
                            >
                                {step.mentionsModel ? '· states the model ✓' : '· model not on page'}
                            </span>
                        )}
                        {step.observedModel && (
                            <span className="ml-1 text-muted-foreground">
                                · family evidence: <span className="font-mono">{step.observedModel}</span>
                            </span>
                        )}
                    </span>
                </>
            );
        case 'link':
            return (
                <>
                    <StepChip>L</StepChip>
                    <span className="min-w-0 [overflow-wrap:anywhere]">
                        {/* The label a human would have clicked IS the link, host as the dim suffix —
                            matching the result step. Links harvested from script blobs have no
                            human-visible label, so the host stands in. */}
                        {step.anchorText ? (
                            <>
                                <TrailLink href={step.href}>“{step.anchorText}”</TrailLink>
                                <span className="text-muted-foreground"> {hostOf(step.href)}</span>
                            </>
                        ) : (
                            <TrailLink href={step.href}>{hostOf(step.href)}</TrailLink>
                        )}
                    </span>
                </>
            );
        case 'document':
            return (
                <>
                    <StepChip>D</StepChip>
                    <span className="min-w-0 [overflow-wrap:anywhere]">
                        <TrailLink href={step.url}>{hostOf(step.url)}</TrailLink>
                        <span className="text-muted-foreground">
                            {' '}
                            {formatBytes(step.byteSize)} · <span className="font-mono">{step.contentHash}</span>
                        </span>
                    </span>
                </>
            );
    }
    // Exhaustive: adding a DiscoveryStep kind without a case fails to compile here.
    return step satisfies never;
};

const StepChip: FC<{ children: React.ReactNode }> = ({ children }) => (
    <span className="inline-flex min-w-6 shrink-0 justify-center rounded bg-muted px-1 py-0.5 font-mono text-[10px] font-bold text-muted-foreground">
        {children}
    </span>
);

const TrailLink: FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="underline-offset-4 hover:underline">
        {children}
    </a>
);

function hostOf(url: string): string {
    return describeUrl(url)?.host ?? url;
}

function formatBytes(byteSize: number): string {
    if (byteSize < 1024 * 1024) return `${Math.max(1, Math.round(byteSize / 1024))}KB`;
    return `${(byteSize / 1024 / 1024).toFixed(1)}MB`;
}
